import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { TableList, Typography, TableListActions, DropdownMenuItem, useDevice, Column } from 'seagull';
import { Edit, Trash, UserAdd } from 'seagull/icons';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import { useIsContactEmailUpdateInProgress } from '~/hooks/usePendingContactEmailUpdates';
import { useLocaleKeys } from '~/i18n';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { Accounting } from '~/types/accounting';
import { eligibilityTooltipProps, eligibilityTooltipsTextsPath } from './ContactsDashboardOld.utils';
import styles from './ContactsDashboardRowOld.module.scss';
import { GetContactsDashboardRowOldTID, hasPortalAccess, portalAccessDisabledTooltip } from './ContactsDashboardRowOld.utils';
export default function ContactsDashboardRowOld(props) {
    var _contact_customerId, _contact_userId;
    const { contact, isQboIntegrated, isXeroIntegrated, qboCustomerIdToDisplay, xeroCustomerIdToDisplay, openContactModal, onDeleteContact, onAddToAnotherClient } = props;
    const isUpdateInProgress = useIsContactEmailUpdateInProgress(contact.userId());
    const { ContactsDashboardRowOldTestIds } = useMemo(()=>GetContactsDashboardRowOldTID(), []);
    const { isMobileDevice } = useDevice();
    const { t: clientViewTexts } = useLocaleKeys('client.view');
    const qboCustomerDisplay = contact.integratedId({
        accounting: Accounting.QBO
    }) ? qboCustomerIdToDisplay[contact.integratedId({
        accounting: Accounting.QBO
    })] : undefined;
    const xeroCustomerDisplay = contact.integratedId({
        accounting: Accounting.Xero
    }) ? xeroCustomerIdToDisplay[contact.integratedId({
        accounting: Accounting.Xero
    })] : undefined;
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { t: rowTexts } = useLocaleKeys('client.contactsDashboardOld.row');
    const { t: statusTexts } = useLocaleKeys('client.contactsDashboardOld.contactStatusFilter');
    const showActionsMenu = !contact.isSample();
    const isEditDisabledWithoutClient = contact.customerId() == null;
    const getEditButtonTooltip = ()=>{
        if (isUpdateInProgress) {
            return {
                title: rowTexts.updateInProgress()
            };
        }
        if (isEditDisabledWithoutClient) {
            return {
                title: rowTexts.editDisabledNoClient()
            };
        }
        return undefined;
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: contact.id(),
        testId: contact.isSample() ? 'sample-contact' : "contact-row",
        "data-heapid": contact.isSample() ? 'sample-contact' : 'contact',
        items: compact([
            {
                value: /*#__PURE__*/ _jsxs("div", {
                    className: styles.companyName,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: "contact-company-name",
                            children: contact.companyName()
                        }),
                        isQboIntegrated && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                            display: qboCustomerDisplay,
                            purpose: "customer",
                            accounting: Accounting.QBO
                        }),
                        isXeroIntegrated && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                            display: xeroCustomerDisplay,
                            purpose: "customer",
                            accounting: Accounting.Xero
                        })
                    ]
                })
            },
            {
                skin: 'standard',
                weight: 'normal',
                testId: 'contact-status',
                value: statusTexts[contact.status()]()
            },
            {
                ellipsis: true,
                skin: 'standard',
                testId: 'contact-contact-name',
                value: contact.fullName()
            },
            {
                value: /*#__PURE__*/ _jsxs(Column, {
                    gap: isMobileDevice ? 8 : 0,
                    testId: "contact-contact-details",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: isMobileDevice ? 'secondary' : undefined,
                            variant: isMobileDevice ? undefined : 'paragraph2',
                            ellipsis: true,
                            children: contact.email()
                        }),
                        contact.phone() && /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            variant: isMobileDevice ? undefined : 'paragraph2',
                            children: contact.phone()
                        })
                    ]
                })
            }
        ]),
        actionColumn: showActionsMenu ? /*#__PURE__*/ _jsx(TableListActions, {
            bi: {
                button: {
                    name: 'contact_view'
                },
                menuTrigger: {
                    name: 'contact_actions'
                }
            },
            testId: "table-list-actions",
            btnHeapId: "contact-view-btn",
            onClickButton: ()=>openContactModal(contact),
            mobile: {
                title: contact.companyName()
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_edit'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    onClick: ()=>openContactModal(contact),
                    testId: ContactsDashboardRowOldTestIds.EditMenuItem(),
                    disabled: isUpdateInProgress || isEditDisabledWithoutClient,
                    tooltipProps: getEditButtonTooltip(),
                    children: "Edit"
                }, "view"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_add_to_another_client'
                    },
                    iconStart: /*#__PURE__*/ _jsx(UserAdd, {}),
                    onClick: ()=>onAddToAnotherClient(contact),
                    testId: ContactsDashboardRowOldTestIds.AddToAnotherClient(),
                    children: rowTexts.addToAnotherClient()
                }, "add-to-another-client"),
                /*#__PURE__*/ _jsx(ClientViewMenuItem, {
                    closeOnClick: true,
                    clientBusinessId: (_contact_customerId = contact.customerId()) !== null && _contact_customerId !== void 0 ? _contact_customerId : null,
                    viewUserId: (_contact_userId = contact.userId()) !== null && _contact_userId !== void 0 ? _contact_userId : undefined,
                    disabled: !hasPortalAccess(contact),
                    disabledTooltip: portalAccessDisabledTooltip({
                        contact,
                        clientViewTexts
                    }),
                    testId: "contact-view-client-portal"
                }, "view-client-portal"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'actions_delete'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    onClick: ()=>onDeleteContact(contact),
                    testId: "actions-delete",
                    disabled: !contact.canDelete(),
                    skin: "error",
                    tooltipProps: eligibilityTooltipProps(contact.deleteEligibility(), eligibilityTooltipsTexts),
                    children: "Delete"
                }, "delete")
            ]
        }) : undefined
    }, contact.id());
}
